import { useState, useEffect, useRef } from "react";

const C = {
  red: "#CC0000",
  darkRed: "#990000",
  black: "#0D0D0D",
  darkGray: "#1a1a1a",
  midGray: "#2a2a2a",
  lightGray: "#888",
  white: "#FFFFFF",
};

const PHOTOS = {
  hero: "https://media.base44.com/images/public/69d6fbd274aedab779e42023/7fb1a0f1f_35B2DA6D-6FD5-49B1-9796-BCACB4153050.jpg",
  jack: "https://media.base44.com/images/public/69d6fbd274aedab779e42023/3f16e078e_3D37A572-0892-4FF5-B1A1-472D468FEF3C_1_105_c.jpg",
  frame: "https://media.base44.com/images/public/69d6fbd274aedab779e42023/ece5e41df_79284EB3-CFE6-4A91-8C10-CF93F8454420_1_105_c.jpeg",
  pinkPart: "https://base44.app/api/apps/69d6fbd274aedab779e42023/files/mp/public/69d6fbd274aedab779e42023/620050974_photo1.jpg",
  pinkSpread: "https://base44.app/api/apps/69d6fbd274aedab779e42023/files/mp/public/69d6fbd274aedab779e42023/70f7b7960_photo2.jpg",
  pinkParts2: "https://base44.app/api/apps/69d6fbd274aedab779e42023/files/mp/public/69d6fbd274aedab779e42023/86defd738_photo3.jpg",
  newWork: "https://base44.app/api/apps/69d6fbd274aedab779e42023/files/mp/public/69d6fbd274aedab779e42023/7d9bc7953_photo4.jpg",
};

const POWDER_COLORS = [
  { name: "Gloss Black", hex: "#0D0D0D", finish: "Gloss" },
  { name: "Matte Black", hex: "#1a1a1a", finish: "Matte" },
  { name: "Satin Black", hex: "#222", finish: "Satin" },
  { name: "Candy Red", hex: "#CC0000", finish: "Gloss" },
  { name: "Matte Red", hex: "#AA0000", finish: "Matte" },
  { name: "Burnt Orange", hex: "#C45000", finish: "Gloss" },
  { name: "Cobalt Blue", hex: "#0047AB", finish: "Gloss" },
  { name: "Midnight Blue", hex: "#1a1a4e", finish: "Satin" },
  { name: "Forest Green", hex: "#2d5a27", finish: "Gloss" },
  { name: "OD Green", hex: "#4a5240", finish: "Matte" },
  { name: "Gloss White", hex: "#F5F5F5", finish: "Gloss" },
  { name: "Satin Silver", hex: "#A8A9AD", finish: "Satin" },
  { name: "Gunmetal", hex: "#4a4a4a", finish: "Satin" },
  { name: "Bronze", hex: "#8C6E3F", finish: "Gloss" },
  { name: "Gold", hex: "#C8A800", finish: "Gloss" },
  { name: "Flat Grey", hex: "#666", finish: "Matte" },
  { name: "Texture Black", hex: "#111", finish: "Texture" },
  { name: "Candy Purple", hex: "#6a0dad", finish: "Gloss" },
  { name: "Hot Pink", hex: "#E91E8C", finish: "Gloss" },
  { name: "Copper", hex: "#B87333", finish: "Metallic" },
];

const SERVICES = [
  { name: "Single Part", desc: "Small bracket, axle, single component", price: "From $40" },
  { name: "Wheel Set (4)", desc: "Full set of 4 wheels, stripped & coated", price: "$400–$450" },
  { name: "eBike Frame", desc: "Full frame sandblast & powder coat", price: "$400–$450" },
  { name: "Lift Kit", desc: "Front & rear crossmembers, spindles, strut springs/spacers", price: "From $350" },
  { name: "Lift Kit+", desc: "Crossmembers, spindles, strut springs/spacers, upper & lower control arms", price: "From $400" },
  { name: "Custom Quote", desc: "Bigger kits, more parts, or anything not listed above", price: "Get Quote →" },
];

const LIFT_KIT_TIERS = [
  {
    name: "Lift Kit",
    tag: "Entry",
    desc: "Front & rear crossmembers, spindles, strut springs/spacers",
    price: "From $350",
    parts: ["Front & rear crossmembers", "Spindles", "Strut springs/spacers"],
  },
  {
    name: "Lift Kit+",
    tag: "Popular",
    desc: "Everything in Lift Kit, plus upper & lower control arms",
    price: "From $400",
    parts: ["Front & rear crossmembers", "Spindles", "Strut springs/spacers", "Upper control arms", "Lower control arms"],
    featured: true,
  },
  {
    name: "Lift Kit++",
    tag: "Full Kit",
    desc: "Everything in Lift Kit+, plus rear blocks, U-bolts & hardware",
    price: "From $400+",
    parts: ["Front & rear crossmembers", "Spindles", "Strut springs/spacers", "Upper & lower control arms", "Rear blocks", "U-bolts + hardware"],
  },
  {
    name: "Lift Kit Pro",
    tag: "Top of Line",
    desc: "The full build — adds leaf springs on top of everything in Lift Kit++",
    price: "From $600",
    parts: ["Front & rear crossmembers", "Spindles", "Strut springs/spacers", "Upper & lower control arms", "Rear blocks + U-bolts", "Leaf springs"],
  },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return isMobile;
}

function FCCBadge({ size = 80 }) {
  const cx = size / 2, cy = size / 2;
  const outerR = size / 2 - 2;
  const innerR = outerR - size * 0.055;
  const fccY = cy - size * 0.06;
  const dividerY = cy + size * 0.14;
  const coatingsY = cy + size * 0.27;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg">
      <circle cx={cx} cy={cy} r={outerR} fill={C.black} />
      <circle cx={cx} cy={cy} r={outerR} fill="none" stroke={C.red} strokeWidth={size * 0.05} />
      <circle cx={cx} cy={cy} r={innerR} fill="none" stroke={C.red} strokeWidth={size * 0.025} />
      <text x={cx} y={fccY} textAnchor="middle" dominantBaseline="central"
        fontFamily="'Arial Black', Impact, Arial, sans-serif" fontWeight="900"
        fontSize={size * 0.295} fill={C.white} letterSpacing={size * 0.01}>FCC</text>
      <line x1={cx - innerR * 0.52} y1={dividerY} x2={cx + innerR * 0.52} y2={dividerY}
        stroke={C.red} strokeWidth={Math.max(size * 0.025, 1)} />
      <text x={cx} y={coatingsY} textAnchor="middle" dominantBaseline="central"
        fontFamily="Arial, sans-serif" fontWeight="700" fontSize={size * 0.085}
        fill={C.white} letterSpacing={size * 0.005}
        textLength={innerR * 1.0} lengthAdjust="spacingAndGlyphs">COATINGS</text>
    </svg>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        backgroundColor: scrolled || menuOpen ? "rgba(13,13,13,0.97)" : "transparent",
        borderBottom: scrolled ? "1px solid #222" : "none",
        transition: "all 0.3s", padding: isMobile ? "0 20px" : "0 40px", height: 64,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <FCCBadge size={36} />
          <div>
            <div style={{ color: C.white, fontWeight: 900, fontSize: 13, letterSpacing: 2, fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase" }}>FALZON'S</div>
            <div style={{ color: C.red, fontWeight: 700, fontSize: 10, letterSpacing: 1.5, fontFamily: "Arial, sans-serif", textTransform: "uppercase", marginTop: -2 }}>CUSTOM COATINGS</div>
          </div>
        </div>

        {isMobile ? (
          <button onClick={() => setMenuOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", padding: 8 }}>
            <div style={{ width: 24, height: 2, backgroundColor: C.white, marginBottom: 5 }} />
            <div style={{ width: 24, height: 2, backgroundColor: C.white, marginBottom: 5 }} />
            <div style={{ width: 24, height: 2, backgroundColor: C.white }} />
          </button>
        ) : (
          <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
            {["Services", "Colors", "Pricing", "Contact"].map(l => (
              <a key={l} href={`#${l.toLowerCase()}`} style={{ color: "#aaa", fontSize: 12, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>{l}</a>
            ))}
            <a href="#quote" style={{ backgroundColor: C.red, color: C.white, padding: "8px 20px", fontSize: 11, fontWeight: 700, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>Get Quote</a>
          </div>
        )}
      </nav>

      {isMobile && menuOpen && (
        <div style={{ position: "fixed", top: 64, left: 0, right: 0, zIndex: 99, backgroundColor: "rgba(13,13,13,0.98)", borderBottom: "1px solid #222", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 0 }}>
          {["Services", "Colors", "Pricing", "Contact"].map(l => (
            <a key={l} href={`#${l.toLowerCase()}`} onClick={() => setMenuOpen(false)}
              style={{ color: "#aaa", fontSize: 14, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", padding: "14px 0", borderBottom: "1px solid #1a1a1a" }}>{l}</a>
          ))}
          <a href="#quote" onClick={() => setMenuOpen(false)}
            style={{ backgroundColor: C.red, color: C.white, padding: "14px 20px", fontSize: 13, fontWeight: 700, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", textAlign: "center", marginTop: 16 }}>
            Get Quote
          </a>
        </div>
      )}
    </>
  );
}

function Hero() {
  const isMobile = useIsMobile();
  return (
    <section style={{ position: "relative", height: "100vh", minHeight: 600, display: "flex", alignItems: "center", overflow: "hidden" }}>
      <img src={PHOTOS.hero} alt="eBike" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
      <div style={{ position: "absolute", inset: 0, background: isMobile ? "rgba(13,13,13,0.82)" : "linear-gradient(to right, rgba(13,13,13,0.93) 50%, rgba(13,13,13,0.35))" }} />
      <div style={{ position: "relative", zIndex: 2, padding: isMobile ? "0 24px" : "0 80px", maxWidth: 640, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 14 : 20, marginBottom: isMobile ? 24 : 32 }}>
          <FCCBadge size={isMobile ? 56 : 80} />
          <div>
            <div style={{ color: C.red, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Pearland, TX</div>
            <div style={{ color: C.white, fontSize: isMobile ? 11 : 13, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>Professional Powder Coating</div>
          </div>
        </div>
        <h1 style={{ color: C.white, fontSize: isMobile ? 36 : 56, fontWeight: 900, lineHeight: 1.05, margin: `0 0 ${isMobile ? 12 : 16}px`, fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase" }}>
          BUILT TOUGH.<br /><span style={{ color: C.red }}>FINISHED SHARP.</span>
        </h1>
        <p style={{ color: "#aaa", fontSize: isMobile ? 14 : 16, lineHeight: 1.7, margin: `0 0 ${isMobile ? 28 : 36}px`, fontFamily: "Arial, sans-serif" }}>
          Custom powder coating for eBikes, trucks, lift kits, and more. Serving the Houston area from Pearland, TX.
        </p>
        <div style={{ display: "flex", gap: 12, flexDirection: isMobile ? "column" : "row" }}>
          <a href="#quote" style={{ backgroundColor: C.red, color: C.white, padding: "14px 32px", fontSize: 13, fontWeight: 700, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", textAlign: "center" }}>
            Get a Free Quote
          </a>
          <a href="#services" style={{ border: "1px solid #444", color: C.white, padding: "14px 32px", fontSize: 13, fontWeight: 700, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", textAlign: "center" }}>
            View Services
          </a>
        </div>
      </div>
    </section>
  );
}

function About() {
  const isMobile = useIsMobile();
  return (
    <section id="contact" style={{ backgroundColor: C.black, padding: isMobile ? "60px 24px" : "100px 80px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80, alignItems: "center" }}>
        <div>
          <div style={{ color: C.red, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 16 }}>About Us</div>
          <h2 style={{ color: C.white, fontSize: isMobile ? 28 : 38, fontWeight: 900, margin: "0 0 24px", fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase", lineHeight: 1.1 }}>
            LOCAL SHOP.<br />REAL RESULTS.
          </h2>
          <p style={{ color: "#888", fontSize: 15, lineHeight: 1.8, margin: "0 0 20px", fontFamily: "Arial, sans-serif" }}>
            Falzon's Custom Coatings is a Pearland-based powder coating shop built for car guys, bike builders, and anyone who wants their parts to look as good as they perform.
          </p>
          <p style={{ color: "#888", fontSize: 15, lineHeight: 1.8, margin: "0 0 32px", fontFamily: "Arial, sans-serif" }}>
            We do everything from single brackets to full frames — prepped, blasted, and coated right. No shortcuts.
          </p>
          <div style={{ display: "flex", gap: 40 }}>
            {[["500+", "Parts Coated"], ["100%", "Local & Personal"], ["48hr", "Avg Turnaround"]].map(([n, l]) => (
              <div key={l}>
                <div style={{ color: C.red, fontSize: 28, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>{n}</div>
                <div style={{ color: "#555", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ position: "relative" }}>
          <img src={PHOTOS.jack} alt="Jack Falzon" style={{ width: "100%", height: isMobile ? 300 : 480, objectFit: "cover", objectPosition: "center top", display: "block" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 80, background: "linear-gradient(to top, rgba(13,13,13,0.7), transparent)" }} />
          <div style={{ position: "absolute", bottom: 16, left: 20 }}>
            <div style={{ color: C.white, fontWeight: 700, fontSize: 14, fontFamily: "Arial, sans-serif" }}>Jack Falzon</div>
            <div style={{ color: C.red, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>Owner & Operator</div>
          </div>
        </div>

        {/* Contact info below photo */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: isMobile ? 24 : 0, gridColumn: isMobile ? "1" : "2" }}>
          <a href="tel:+17138823676" style={{ display: "flex", alignItems: "center", gap: 14, textDecoration: "none", backgroundColor: C.darkGray, padding: "16px 20px" }}>
            <div style={{ width: 36, height: 36, backgroundColor: C.red, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.27 9.09a19.79 19.79 0 01-3.07-8.67A2 2 0 012.18 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
            </div>
            <div>
              <div style={{ color: "#555", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 2 }}>Call or Text</div>
              <div style={{ color: C.white, fontSize: 15, fontWeight: 700, fontFamily: "'Arial Black', Arial, sans-serif" }}>+1 (713) 882-3676</div>
            </div>
          </a>
          <a href="mailto:jack@falzoncustomcoatings.com" style={{ display: "flex", alignItems: "center", gap: 14, textDecoration: "none", backgroundColor: C.darkGray, padding: "16px 20px" }}>
            <div style={{ width: 36, height: 36, backgroundColor: "#222", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#CC0000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div>
              <div style={{ color: "#555", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 2 }}>Email Us</div>
              <div style={{ color: C.white, fontSize: 13, fontWeight: 700, fontFamily: "Arial, sans-serif" }}>jack@falzoncustomcoatings.com</div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const isMobile = useIsMobile();
  return (
    <section id="services" style={{ backgroundColor: C.darkGray, padding: isMobile ? "60px 24px" : "100px 80px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{ color: C.red, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 12 }}>What We Do</div>
          <h2 style={{ color: C.white, fontSize: isMobile ? 28 : 38, fontWeight: 900, margin: 0, fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase" }}>SERVICES</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 2 }}>
          {SERVICES.map((s, i) => (
            s.name === "Custom Quote" ? (
              <a key={i} href="#quote" style={{ backgroundColor: C.black, padding: "36px 32px", borderTop: "3px solid #222", textDecoration: "none", display: "block", cursor: "pointer" }}>
                <div style={{ color: C.red, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 12 }}>★ GET A QUOTE</div>
                <div style={{ color: C.white, fontSize: 18, fontWeight: 700, margin: "0 0 10px", fontFamily: "'Arial Black', Arial, sans-serif" }}>{s.name}</div>
                <div style={{ color: "#666", fontSize: 13, lineHeight: 1.6, margin: "0 0 24px", fontFamily: "Arial, sans-serif" }}>{s.desc}</div>
                <div style={{ color: C.red, fontSize: 20, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>{s.price}</div>
              </a>
            ) : (
              <div key={i} style={{ backgroundColor: C.black, padding: "36px 32px", borderTop: "3px solid #222" }}>
                <div style={{ color: C.red, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 12 }}>{`Service 0${i + 1}`}</div>
                <div style={{ color: C.white, fontSize: 18, fontWeight: 700, margin: "0 0 10px", fontFamily: "'Arial Black', Arial, sans-serif" }}>{s.name}</div>
                <div style={{ color: "#666", fontSize: 13, lineHeight: 1.6, margin: "0 0 24px", fontFamily: "Arial, sans-serif" }}>{s.desc}</div>
                <div style={{ color: C.white, fontSize: 20, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>{s.price}</div>
              </div>
            )
          ))}
        </div>
        <div style={{ marginTop: 2, backgroundColor: C.black, padding: "40px 32px", borderLeft: `4px solid ${C.red}`, display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", gap: 24, flexDirection: isMobile ? "column" : "row" }}>
          <div>
            <div style={{ color: C.red, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 8 }}>Flexible Options</div>
            <div style={{ color: C.white, fontSize: isMobile ? 16 : 20, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif", marginBottom: 8 }}>BRING YOUR OWN POWDER — OR WE'LL GET IT</div>
            <div style={{ color: "#666", fontSize: 14, fontFamily: "Arial, sans-serif", lineHeight: 1.7 }}>
              Already have a color in mind? Bring your own. Don't want the hassle? We source it for you.
            </div>
          </div>
          <div style={{ flexShrink: 0, textAlign: isMobile ? "left" : "center" }}>
            <div style={{ color: "#444", fontSize: 12, fontFamily: "Arial, sans-serif", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Powder sourcing fee</div>
            <div style={{ color: C.white, fontSize: 28, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>+$15–$35</div>
            <div style={{ color: "#444", fontSize: 11, fontFamily: "Arial, sans-serif" }}>depending on color/brand</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ColorWheel() {
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => setActive(a => (a + 1) % POWDER_COLORS.length), 1800);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPaused]);

  return (
    <section id="colors" style={{ backgroundColor: C.black, padding: isMobile ? "60px 24px" : "100px 80px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{ color: C.red, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 12 }}>Available Finishes</div>
          <h2 style={{ color: C.white, fontSize: isMobile ? 28 : 38, fontWeight: 900, margin: 0, fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase" }}>POWDER COLORS</h2>
          <p style={{ color: "#555", fontSize: 13, marginTop: 12, fontFamily: "Arial, sans-serif" }}>20+ standard colors · Custom colors available · Bring your own powder</p>
        </div>
        <div style={{ display: "flex", gap: isMobile ? 24 : 60, alignItems: "center", marginBottom: 48, flexDirection: isMobile ? "column" : "row" }}>
          <div style={{ flex: 1, width: "100%" }}>
            <div style={{
              height: isMobile ? 200 : 280, backgroundColor: POWDER_COLORS[active].hex,
              border: "1px solid #222", transition: "background-color 0.4s ease",
              display: "flex", alignItems: "flex-end", padding: 24,
              backgroundImage: POWDER_COLORS[active].finish === "Texture"
                ? "repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 6px)"
                : "none",
            }}>
              <div>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>{POWDER_COLORS[active].finish}</div>
                <div style={{ color: C.white, fontSize: 24, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif", textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{POWDER_COLORS[active].name}</div>
              </div>
            </div>
          </div>
          <div style={{ flex: 1, width: "100%" }}>
            <div style={{ color: C.white, fontSize: 22, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif", marginBottom: 12 }}>{POWDER_COLORS[active].name.toUpperCase()}</div>
            <div style={{ display: "inline-block", backgroundColor: "#1a1a1a", color: C.red, padding: "4px 12px", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 20 }}>{POWDER_COLORS[active].finish} Finish</div>
            <p style={{ color: "#666", fontSize: 14, lineHeight: 1.7, fontFamily: "Arial, sans-serif", marginBottom: 24 }}>
              Available in standard or custom application. Bring your own powder or let us source it for you.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setActive(a => (a - 1 + POWDER_COLORS.length) % POWDER_COLORS.length)}
                style={{ backgroundColor: "#1a1a1a", border: "1px solid #333", color: C.white, padding: "10px 20px", cursor: "pointer", fontSize: 18 }}>←</button>
              <button onClick={() => setIsPaused(p => !p)}
                style={{ backgroundColor: isPaused ? C.red : "#1a1a1a", border: `1px solid ${isPaused ? C.red : "#333"}`, color: C.white, padding: "10px 20px", cursor: "pointer", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>
                {isPaused ? "▶ Play" : "⏸ Pause"}
              </button>
              <button onClick={() => setActive(a => (a + 1) % POWDER_COLORS.length)}
                style={{ backgroundColor: "#1a1a1a", border: "1px solid #333", color: C.white, padding: "10px 20px", cursor: "pointer", fontSize: 18 }}>→</button>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {POWDER_COLORS.map((col, i) => (
            <div key={i} onClick={() => { setActive(i); setIsPaused(true); }} title={col.name}
              style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, backgroundColor: col.hex, cursor: "pointer", border: i === active ? `2px solid ${C.red}` : "2px solid transparent", transition: "border 0.2s", flexShrink: 0 }} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  const [lightbox, setLightbox] = useState(null);
  const isMobile = useIsMobile();
  const photos = [
    { src: PHOTOS.pinkSpread, label: "Lift Kit — Cobalt Blue" },
    { src: PHOTOS.frame, label: "eBike Frame — Gloss Black" },
    { src: PHOTOS.pinkPart, label: "Custom Part — Cobalt Blue" },
    { src: PHOTOS.hero, label: "eBike Build" },
    { src: PHOTOS.pinkParts2, label: "Lift Kit Hardware — Hot Pink" },
    { src: PHOTOS.newWork, label: "Custom Work" },
    { src: PHOTOS.jack, label: "Jack Falzon — Owner" },
  ];

  return (
    <section style={{ backgroundColor: C.darkGray, padding: isMobile ? "60px 24px" : "100px 80px" }}>
      {lightbox !== null && (
        <div onClick={() => setLightbox(null)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.92)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <img src={photos[lightbox].src} alt="" style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }} />
        </div>
      )}
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{ color: C.red, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 12 }}>Our Work</div>
          <h2 style={{ color: C.white, fontSize: isMobile ? 28 : 38, fontWeight: 900, margin: 0, fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase" }}>GALLERY</h2>
        </div>
        {isMobile ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            {photos.map((p, i) => (
              <div key={i} onClick={() => setLightbox(i)} style={{ cursor: "pointer", overflow: "hidden", position: "relative", height: 160 }}>
                <img src={p.src} alt={p.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 8px", background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}>
                  <div style={{ color: C.white, fontSize: 10, fontFamily: "Arial, sans-serif" }}>{p.label}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gridTemplateRows: "260px 260px", gap: 4 }}>
            <div onClick={() => setLightbox(0)} style={{ gridRow: "1 / 3", cursor: "pointer", overflow: "hidden", position: "relative" }}>
              <img src={photos[0].src} alt={photos[0].label} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s" }}
                onMouseEnter={e => e.target.style.transform = "scale(1.04)"}
                onMouseLeave={e => e.target.style.transform = "scale(1)"} />
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "20px 16px", background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}>
                <div style={{ color: C.white, fontSize: 12, fontFamily: "Arial, sans-serif" }}>{photos[0].label}</div>
              </div>
            </div>
            {photos.slice(1, 5).map((p, i) => (
              <div key={i} onClick={() => setLightbox(i + 1)} style={{ cursor: "pointer", overflow: "hidden", position: "relative" }}>
                <img src={p.src} alt={p.label} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s" }}
                  onMouseEnter={e => e.target.style.transform = "scale(1.04)"}
                  onMouseLeave={e => e.target.style.transform = "scale(1)"} />
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 10px", background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}>
                  <div style={{ color: C.white, fontSize: 11, fontFamily: "Arial, sans-serif" }}>{p.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Pricing() {
  const isMobile = useIsMobile();

  const basePricing = [
    { name: "Single Part", desc: "Small bracket, axle, single component", price: "From $40" },
    { name: "Wheel Set (4)", desc: "Full set of 4 wheels, stripped & coated", price: "$400–$450" },
    { name: "eBike Frame", desc: "Full frame sandblast & powder coat", price: "$400–$450" },
    { name: "Custom Quote", desc: "Bigger kits, more parts, or anything not listed", price: "Get Quote →", highlight: true },
  ];

  return (
    <section id="pricing" style={{ backgroundColor: C.black, padding: isMobile ? "60px 24px" : "100px 80px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", marginBottom: 60, flexDirection: isMobile ? "column" : "row", gap: 16 }}>
          <div>
            <div style={{ color: C.red, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 12 }}>Transparent Pricing</div>
            <h2 style={{ color: C.white, fontSize: isMobile ? 28 : 38, fontWeight: 900, margin: 0, fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase" }}>PRICING GUIDE</h2>
          </div>
          <a href="#quote" style={{ backgroundColor: C.red, color: C.white, padding: "12px 28px", fontSize: 12, fontWeight: 700, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", flexShrink: 0 }}>Get Custom Quote</a>
        </div>

        {/* Base Pricing */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 2, marginBottom: 40 }}>
          {basePricing.map((s, i) => (
            s.highlight ? (
              <a key={i} href="#quote" style={{ backgroundColor: C.darkGray, padding: "28px", borderTop: "3px solid #222", textDecoration: "none", display: "block", cursor: "pointer" }}>
                <div style={{ color: C.white, fontSize: 15, fontWeight: 700, fontFamily: "'Arial Black', Arial, sans-serif", marginBottom: 8 }}>{s.name}</div>
                <div style={{ color: "#555", fontSize: 12, fontFamily: "Arial, sans-serif", marginBottom: 16, lineHeight: 1.6 }}>{s.desc}</div>
                <div style={{ color: C.red, fontSize: 20, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>{s.price}</div>
              </a>
            ) : (
              <div key={i} style={{ backgroundColor: C.darkGray, padding: "28px", borderTop: "3px solid #222" }}>
                <div style={{ color: C.white, fontSize: 15, fontWeight: 700, fontFamily: "'Arial Black', Arial, sans-serif", marginBottom: 8 }}>{s.name}</div>
                <div style={{ color: "#555", fontSize: 12, fontFamily: "Arial, sans-serif", marginBottom: 16, lineHeight: 1.6 }}>{s.desc}</div>
                <div style={{ color: C.white, fontSize: 20, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>{s.price}</div>
              </div>
            )
          ))}
        </div>

        {/* Lift Kit Tiers */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: C.red, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 8 }}>Lift Kit Packages</div>
          <div style={{ color: C.white, fontSize: isMobile ? 18 : 22, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif", marginBottom: 4 }}>CHOOSE YOUR BUILD LEVEL</div>
          <div style={{ color: "#555", fontSize: 13, fontFamily: "Arial, sans-serif", marginBottom: 24 }}>Each tier adds onto the previous. Bigger kit? Send photos and we'll quote it.</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)", gap: 2 }}>
          {LIFT_KIT_TIERS.map((t, i) => (
            <div key={i} style={{ backgroundColor: t.featured ? "#1a0000" : C.darkGray, padding: "28px", borderTop: `3px solid ${t.featured ? C.red : "#333"}`, position: "relative" }}>
              {t.featured && (
                <div style={{ position: "absolute", top: -1, right: 0, backgroundColor: C.red, color: C.white, fontSize: 9, fontWeight: 700, padding: "3px 10px", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>POPULAR</div>
              )}
              <div style={{ color: t.featured ? C.red : "#444", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 8 }}>{t.tag}</div>
              <div style={{ color: C.white, fontSize: 16, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif", marginBottom: 16 }}>{t.name}</div>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px" }}>
                {t.parts.map((p, j) => (
                  <li key={j} style={{ color: "#777", fontSize: 12, fontFamily: "Arial, sans-serif", padding: "4px 0", display: "flex", alignItems: "flex-start", gap: 8, borderBottom: j < t.parts.length - 1 ? "1px solid #222" : "none" }}>
                    <span style={{ color: C.red, flexShrink: 0, marginTop: 1 }}>·</span>{p}
                  </li>
                ))}
              </ul>
              <div style={{ color: t.featured ? C.red : C.white, fontSize: 22, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>{t.price}</div>
            </div>
          ))}
        </div>

        {/* Big Kit CTA */}
        <div style={{ marginTop: 2, backgroundColor: C.darkGray, padding: "28px 32px", borderLeft: `4px solid ${C.red}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, flexDirection: isMobile ? "column" : "row" }}>
          <div>
            <div style={{ color: C.white, fontSize: 15, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif", marginBottom: 6 }}>GOT A BIGGER KIT?</div>
            <div style={{ color: "#555", fontSize: 13, fontFamily: "Arial, sans-serif" }}>Full suspension builds, custom big lift kits, or parts not listed — send photos and we'll get you a custom price within 24 hours.</div>
          </div>
          <a href="#quote" style={{ backgroundColor: C.red, color: C.white, padding: "12px 28px", fontSize: 12, fontWeight: 700, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", flexShrink: 0, whiteSpace: "nowrap" }}>Get Custom Quote →</a>
        </div>
      </div>
    </section>
  );
}

function QuoteForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", details: "", powder: "we_source" });
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();
  const isMobile = useIsMobile();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (submitted) return (
    <section id="quote" style={{ backgroundColor: C.darkGray, padding: isMobile ? "60px 24px" : "100px 80px" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>✅</div>
        <h2 style={{ color: C.white, fontSize: 28, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>QUOTE REQUEST SENT</h2>
        <p style={{ color: "#666", fontSize: 14, fontFamily: "Arial, sans-serif", lineHeight: 1.7 }}>We'll review your photos and get back to you within 24 hours with a price.</p>
        <button onClick={() => setSubmitted(false)} style={{ marginTop: 24, backgroundColor: C.red, color: C.white, border: "none", padding: "12px 28px", fontSize: 12, fontWeight: 700, cursor: "pointer", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>Submit Another</button>
      </div>
    </section>
  );

  return (
    <section id="quote" style={{ backgroundColor: C.darkGray, padding: isMobile ? "60px 24px" : "100px 80px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80 }}>
        <div>
          <div style={{ color: C.red, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 16 }}>Free Estimate</div>
          <h2 style={{ color: C.white, fontSize: isMobile ? 28 : 38, fontWeight: 900, margin: "0 0 20px", fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase", lineHeight: 1.1 }}>GET YOUR<br />CUSTOM QUOTE</h2>
          <p style={{ color: "#666", fontSize: 14, lineHeight: 1.8, fontFamily: "Arial, sans-serif", marginBottom: 32 }}>
            Send us photos, pick your color, and we'll get back to you within 24 hours. No commitment required.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[["📷", "Send photos of your parts"], ["🎨", "Tell us your color & finish"], ["💬", "Get a price within 24hrs"], ["🔧", "Drop off & we handle the rest"]].map(([icon, text]) => (
              <div key={text} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <span style={{ fontSize: 20 }}>{icon}</span>
                <span style={{ color: "#888", fontSize: 13, fontFamily: "Arial, sans-serif" }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={async e => { e.preventDefault(); setLoading(true); try { const toBase64 = f => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res({ name: f.name, type: f.type, data: r.result.split(",")[1] }); r.onerror = rej; r.readAsDataURL(f); }); const photos = await Promise.all(files.map(toBase64)); await fetch("https://superagent-79e42023.base44.app/api/functions/fccQuoteEmail", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, photos }) }); } catch(err) { console.error(err); } setLoading(false); setSubmitted(true); }} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[["name", "Full Name", "text"], ["phone", "Phone Number", "tel"], ["email", "Email Address", "email"]].map(([key, label, type]) => (
            <div key={key}>
              <label style={{ color: "#555", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", display: "block", marginBottom: 6 }}>{label}</label>
              <input type={type} required value={form[key]} onChange={e => set(key, e.target.value)}
                style={{ width: "100%", backgroundColor: "#111", border: "1px solid #2a2a2a", color: C.white, padding: "12px 16px", fontSize: 14, fontFamily: "Arial, sans-serif", boxSizing: "border-box", outline: "none" }} />
            </div>
          ))}
          <div>
            <label style={{ color: "#555", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", display: "block", marginBottom: 8 }}>Powder Coating Option</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[["we_source", "We Source It (+$15–$35)"], ["bring_own", "I'll Bring My Own"]].map(([val, label]) => (
                <button key={val} type="button" onClick={() => set("powder", val)}
                  style={{ flex: 1, padding: "12px 8px", fontSize: isMobile ? 11 : 12, fontFamily: "Arial, sans-serif", cursor: "pointer", fontWeight: 700, backgroundColor: form.powder === val ? C.red : "#111", color: C.white, border: `1px solid ${form.powder === val ? C.red : "#2a2a2a"}` }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ color: "#555", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", display: "block", marginBottom: 6 }}>Job Details & Color</label>
            <textarea value={form.details} onChange={e => set("details", e.target.value)} rows={4} required
              placeholder="Describe what you want coated and what color/finish you're looking for..."
              style={{ width: "100%", backgroundColor: "#111", border: "1px solid #2a2a2a", color: C.white, padding: "12px 16px", fontSize: 14, fontFamily: "Arial, sans-serif", boxSizing: "border-box", outline: "none", resize: "vertical" }} />
          </div>
          <div>
            <label style={{ color: "#555", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", display: "block", marginBottom: 6 }}>Upload Photos of Your Parts</label>
            <div onClick={() => fileRef.current.click()} style={{ backgroundColor: "#111", border: "2px dashed #2a2a2a", padding: "28px 20px", textAlign: "center", cursor: "pointer" }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>📎</div>
              <div style={{ color: "#555", fontSize: 12, fontFamily: "Arial, sans-serif" }}>
                {files.length > 0 ? `${files.length} file(s) selected` : "Tap to upload photos (JPG, PNG, HEIC)"}
              </div>
            </div>
            <input ref={fileRef} type="file" multiple accept="image/*" style={{ display: "none" }} onChange={e => setFiles(Array.from(e.target.files))} />
          </div>
          <button type="submit" disabled={loading} style={{ backgroundColor: C.red, color: C.white, border: "none", padding: "16px", fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginTop: 8, opacity: loading ? 0.7 : 1 }}>
            {loading ? "Sending..." : "Send Quote Request →"}
          </button>
        </form>
      </div>
    </section>
  );
}

const CHECKOUT_ITEMS = [
  { key: "single_part",    label: "Single Part",        price: 40,  display: "From $40",    desc: "Bracket, axle, small component" },
  { key: "wheel_set",      label: "Wheel Set (4)",      price: 425, display: "$400–$450",   desc: "Full set stripped & coated" },
  { key: "ebike_frame",    label: "eBike Frame",        price: 425, display: "$400–$450",   desc: "Full frame sandblast & coat" },
  { key: "lift_kit",       label: "Lift Kit",           price: 350, display: "From $350",   desc: "Crossmembers, spindles, struts" },
  { key: "lift_kit_plus",  label: "Lift Kit+",          price: 400, display: "From $400",   desc: "Lift Kit + upper/lower control arms", popular: true },
  { key: "lift_kit_pp",    label: "Lift Kit++",         price: 400, display: "From $400+",  desc: "Lift Kit+ + rear blocks & U-bolts" },
  { key: "lift_kit_pro",   label: "Lift Kit Pro",       price: 600, display: "From $600",   desc: "Full build including leaf springs" },
  { key: "powder_sourcing",label: "Powder Sourcing",    price: 25,  display: "+$15–$35",    desc: "We source your powder color" },
];

function Payment() {
  const isMobile = useIsMobile();
  const [cart, setCart] = useState({});
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedColor, setSelectedColor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toggle = (key) => setCart(c => ({ ...c, [key]: c[key] ? 0 : 1 }));
  const isIn = (key) => cart[key] > 0;

  const cartItems = CHECKOUT_ITEMS.filter(i => isIn(i.key));
  const total = cartItems.reduce((sum, i) => sum + i.price * (cart[i.key] || 1), 0);

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("https://superagent-79e42023.base44.app/api/functions/fccCheckout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: cartItems.map(i => ({ key: i.key, qty: cart[i.key] || 1 })),
          customer_email: email,
          notes: [selectedColor && selectedColor !== "custom" ? `Color: ${selectedColor}` : selectedColor === "custom" ? "Color: Custom (see notes)" : "", notes].filter(Boolean).join(" | "),
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch (e) {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="checkout" style={{ backgroundColor: C.black, padding: isMobile ? "60px 24px" : "100px 80px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ color: C.red, fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 12 }}>Online Checkout</div>
          <h2 style={{ color: C.white, fontSize: isMobile ? 28 : 38, fontWeight: 900, margin: "0 0 12px", fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase" }}>BUILD YOUR ORDER</h2>
          <p style={{ color: "#555", fontSize: 13, fontFamily: "Arial, sans-serif" }}>Select your services below, then pay securely online. Balance always due on pickup.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 360px", gap: 24, alignItems: "start" }}>
          {/* Item Selector */}
          <div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
              {CHECKOUT_ITEMS.map((item) => {
                const selected = isIn(item.key);
                return (
                  <div key={item.key} onClick={() => toggle(item.key)}
                    style={{
                      backgroundColor: selected ? "#1a0000" : C.darkGray,
                      padding: "24px",
                      cursor: "pointer",
                      borderTop: `3px solid ${selected ? C.red : "#222"}`,
                      transition: "all 0.15s",
                      position: "relative",
                      userSelect: "none",
                    }}>
                    {item.popular && (
                      <div style={{ position: "absolute", top: 0, right: 0, backgroundColor: C.red, color: C.white, fontSize: 9, fontWeight: 700, padding: "3px 10px", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>POPULAR</div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div style={{ color: C.white, fontSize: 15, fontWeight: 700, fontFamily: "'Arial Black', Arial, sans-serif" }}>{item.label}</div>
                      <div style={{
                        width: 22, height: 22, border: `2px solid ${selected ? C.red : "#444"}`,
                        backgroundColor: selected ? C.red : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, marginLeft: 8,
                        color: C.white, fontSize: 14, fontWeight: 900,
                      }}>{selected ? "✓" : ""}</div>
                    </div>
                    <div style={{ color: "#555", fontSize: 12, fontFamily: "Arial, sans-serif", marginBottom: 10, lineHeight: 1.5 }}>{item.desc}</div>
                    <div style={{ color: selected ? C.red : C.white, fontSize: 18, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>{item.display}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cart / Checkout */}
          <div style={{ backgroundColor: C.darkGray, padding: "28px", position: isMobile ? "relative" : "sticky", top: 80 }}>
            <div style={{ color: C.white, fontSize: 14, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif", letterSpacing: 1, textTransform: "uppercase", marginBottom: 20, borderBottom: "1px solid #333", paddingBottom: 16 }}>
              Your Order
            </div>

            {cartItems.length === 0 ? (
              <div style={{ color: "#444", fontSize: 13, fontFamily: "Arial, sans-serif", textAlign: "center", padding: "24px 0" }}>
                ← Select services to add them
              </div>
            ) : (
              <div style={{ marginBottom: 20 }}>
                {cartItems.map(item => (
                  <div key={item.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #333" }}>
                    <div>
                      <div style={{ color: C.white, fontSize: 13, fontFamily: "Arial, sans-serif", fontWeight: 700 }}>{item.label}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ color: C.white, fontSize: 13, fontFamily: "'Arial Black', Arial, sans-serif" }}>${item.price}</span>
                      <button onClick={(e) => { e.stopPropagation(); toggle(item.key); }}
                        style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "2px 4px" }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ borderTop: "1px solid #333", paddingTop: 16, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: "#666", fontSize: 12, fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: 1 }}>Estimated Total</span>
                <span style={{ color: C.white, fontSize: 20, fontWeight: 900, fontFamily: "'Arial Black', Arial, sans-serif" }}>${total.toLocaleString()}</span>
              </div>
              <div style={{ color: "#444", fontSize: 11, fontFamily: "Arial, sans-serif" }}>Balance due on pickup · Prices may vary by job</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ color: "#555", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", display: "block", marginBottom: 6 }}>Email (optional)</label>
              <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)}
                style={{ width: "100%", backgroundColor: "#111", border: "1px solid #2a2a2a", color: C.white, padding: "10px 14px", fontSize: 13, fontFamily: "Arial, sans-serif", boxSizing: "border-box", outline: "none" }} />
            </div>

            {/* Color Picker */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ color: "#555", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", display: "block", marginBottom: 10 }}>Powder Color</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: selectedColor ? 10 : 0 }}>
                {POWDER_COLORS.map((col) => (
                  <button key={col.name} title={`${col.name} — ${col.finish}`}
                    onClick={() => setSelectedColor(c => c === col.name ? null : col.name)}
                    style={{
                      width: 28, height: 28,
                      backgroundColor: col.hex,
                      border: selectedColor === col.name ? `2px solid ${C.red}` : "2px solid #222",
                      cursor: "pointer",
                      outline: selectedColor === col.name ? `1px solid ${C.red}` : "none",
                      outlineOffset: 1,
                      boxSizing: "border-box",
                      flexShrink: 0,
                    }} />
                ))}
                <button title="Custom / not listed"
                  onClick={() => setSelectedColor(c => c === "custom" ? null : "custom")}
                  style={{
                    width: 28, height: 28,
                    background: "linear-gradient(135deg, #f00,#ff0,#0f0,#0ff,#00f,#f0f)",
                    border: selectedColor === "custom" ? `2px solid ${C.red}` : "2px solid #222",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    flexShrink: 0,
                  }} />
              </div>
              {selectedColor && selectedColor !== "custom" && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", backgroundColor: "#111", border: "1px solid #2a2a2a" }}>
                  <div style={{ width: 16, height: 16, backgroundColor: POWDER_COLORS.find(c => c.name === selectedColor)?.hex, flexShrink: 0, border: "1px solid #333" }} />
                  <div>
                    <span style={{ color: C.white, fontSize: 12, fontFamily: "Arial, sans-serif", fontWeight: 700 }}>{selectedColor}</span>
                    <span style={{ color: "#555", fontSize: 11, fontFamily: "Arial, sans-serif", marginLeft: 8 }}>{POWDER_COLORS.find(c => c.name === selectedColor)?.finish} Finish</span>
                  </div>
                </div>
              )}
              {selectedColor === "custom" && (
                <div style={{ padding: "8px 12px", backgroundColor: "#111", border: "1px solid #2a2a2a" }}>
                  <span style={{ color: "#888", fontSize: 12, fontFamily: "Arial, sans-serif" }}>Describe your color in the notes below</span>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ color: "#555", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", display: "block", marginBottom: 6 }}>Notes / Specific Parts</label>
              <textarea rows={3} placeholder="Part details, special requests..." value={notes} onChange={e => setNotes(e.target.value)}
                style={{ width: "100%", backgroundColor: "#111", border: "1px solid #2a2a2a", color: C.white, padding: "10px 14px", fontSize: 13, fontFamily: "Arial, sans-serif", boxSizing: "border-box", outline: "none", resize: "vertical" }} />
            </div>

            {error && <div style={{ color: C.red, fontSize: 12, fontFamily: "Arial, sans-serif", marginBottom: 12 }}>{error}</div>}

            <button onClick={handleCheckout} disabled={cartItems.length === 0 || loading}
              style={{
                width: "100%", backgroundColor: cartItems.length === 0 ? "#333" : C.red,
                color: C.white, border: "none", padding: "16px", fontSize: 13,
                fontWeight: 700, cursor: cartItems.length === 0 ? "not-allowed" : "pointer",
                letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif",
                opacity: loading ? 0.7 : 1,
              }}>
              {loading ? "Redirecting..." : cartItems.length === 0 ? "Select a Service" : `Pay $${total.toLocaleString()} →`}
            </button>

            <div style={{ color: "#333", fontSize: 11, fontFamily: "Arial, sans-serif", textAlign: "center", marginTop: 12 }}>
              🔒 Secured by Stripe · All major cards accepted
            </div>

            <div style={{ borderTop: "1px solid #222", marginTop: 20, paddingTop: 16, textAlign: "center" }}>
              <div style={{ color: "#444", fontSize: 12, fontFamily: "Arial, sans-serif", marginBottom: 10 }}>Have a bigger kit or something not listed?</div>
              <a href="#quote" style={{ display: "inline-block", border: "1px solid #333", color: "#888", padding: "10px 20px", fontSize: 11, fontWeight: 700, textDecoration: "none", letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif" }}>
                Get a Custom Quote ↓
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const isMobile = useIsMobile();
  return (
    <footer style={{ backgroundColor: "#050505", padding: isMobile ? "40px 24px 32px" : "60px 80px 40px", borderTop: "1px solid #1a1a1a" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "flex-start", marginBottom: 48, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 32 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <FCCBadge size={56} />
            <div>
              <div style={{ color: C.white, fontWeight: 900, fontSize: isMobile ? 13 : 16, letterSpacing: 2, fontFamily: "'Arial Black', Arial, sans-serif", textTransform: "uppercase" }}>FALZON'S CUSTOM COATINGS</div>
              <div style={{ color: "#444", fontSize: 12, fontFamily: "Arial, sans-serif", marginTop: 4 }}>Pearland, TX · Serving Greater Houston</div>
            </div>
          </div>
          <div style={{ textAlign: isMobile ? "left" : "right" }}>
            <div style={{ color: "#444", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontFamily: "Arial, sans-serif", marginBottom: 8 }}>Get in Touch</div>
            <div style={{ color: C.white, fontSize: 14, fontFamily: "Arial, sans-serif", marginBottom: 4 }}>+1 (713) 882-3676</div>
            <div style={{ color: "#666", fontSize: 13, fontFamily: "Arial, sans-serif" }}>jack@falzoncustomcoatings.com</div>
          </div>
        </div>
        <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ color: "#333", fontSize: 11, fontFamily: "Arial, sans-serif" }}>© 2026 Falzon's Custom Coatings.</div>
            <a href="https://kineticseo.co" target="_blank" rel="noopener noreferrer" style={{ color: "#333", fontSize: 11, fontFamily: "Arial, sans-serif", textDecoration: "none" }}>Made by <span style={{ color: "#CC0000" }}>Kinetic SEO</span></a>
          </div>
          <div style={{ color: "#333", fontSize: 11, fontFamily: "Arial, sans-serif" }}>Pearland, TX</div>
        </div>
      </div>
    </footer>
  );
}

export default function FalzonSite() {
  return (
    <div style={{ backgroundColor: C.black, minHeight: "100vh" }}>
      <Nav />
      <Hero />
      <About />
      <Services />
      <ColorWheel />
      <Gallery />
      <Pricing />
      <Payment />
      <QuoteForm />
      <Footer />
    </div>
  );
}
